import React from 'react';
import { format } from 'date-fns';
import { MessageSquare, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

// Message type definition
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  lastUpdated: Date;
  metadata?: {
    tags?: string[];
    status?: 'active' | 'archived' | 'deleted';
    source?: string;
  };
}

interface ConversationListProps {
  conversations: Conversation[];
  onSelectConversation: (conversation: Conversation) => void;
  onStartNew: (title: string) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  onSelectConversation,
  onStartNew,
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">{t('chatbot.yourConversations')}</h2>
      </div>

      <Button
        onClick={() => onStartNew('')}
        className="w-full justify-start mb-4 bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:from-pink-600 hover:to-orange-600 transition-all duration-200 border-0 font-semibold shadow-lg"
        style={{
          background: 'linear-gradient(to right, #FF1493, #FFA500)',
        }}
      >
        <Plus className="mr-2 h-4 w-4" />
        {t('chatbot.newConversation')}
      </Button>

      {conversations.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground/60" />
          <p className="mt-2 text-sm text-muted-foreground">{t('chatbot.noConversationsYet')}</p>
          <p className="text-xs text-muted-foreground">{t('chatbot.startNewConversation')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {conversations.map((conversation) => (
            <button
              key={conversation.id}
              className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gebeya-pink/20"
              onClick={() => onSelectConversation(conversation)}
            >
              <div className="truncate font-medium text-sm">{conversation.title}</div>
              <div className="flex items-center justify-between mt-1">
                <div className="text-xs text-muted-foreground truncate max-w-[70%]">
                  {conversation.messages[conversation.messages.length - 1]?.text.slice(0, 30)}
                  {conversation.messages[conversation.messages.length - 1]?.text.length > 30 ? '...' : ''}
                </div>
                <div className="text-xs text-muted-foreground">
                  {format(conversation.lastUpdated, 'MMM d')}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConversationList; 